// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/SpecialOffers.tsx
// 🔧 PURPOSE: Le offerte della Home nello stile delle brochure delle email:
//             la Fuga romantica (due riquadri crema, WEEKEND e SETTIMANA,
//             e "Scopri l'offerta" che apre la pagina a tutto schermo
//             /offers/romantic, pages/RomanticEscape) e la Gift card (+10%
//             di bonus, REGALA → /gift-card).
//
//             `variant` sceglie titoli e peso, vista per vista:
//             - guest: "Offerte · Vieni a trovarci", senza foto (la foto è
//               già nel carosello), bottone a filo;
//             - before: "Offerte · Offerte riservate agli ospiti", come guest
//               (in fondo alla pagina, prima dell'arrivo non deve pesare);
//             - stay / after: "Per il prossimo viaggio · Offerte riservate
//               agli ospiti", con la foto (i due calici al tramonto, la
//               stessa della pagina) e il bottone pieno; nel soggiorno
//               anche "Prenota il prossimo soggiorno" → /book (dopo la
//               partenza c'è già "Prenota di nuovo" più in alto).
//
//             Prima erano due card a gradiente in un carosello: nel nuovo
//             aspetto stanno una sotto l'altra, lette in un colpo d'occhio.
// ============================================

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Gift } from "lucide-react";
import { intlLocale } from "../lib/locale";
import { formatWholeEuro, ROMANTIC_IMAGE, ROMANTIC_PATH, ROMANTIC_PRICES } from "../lib/romanticOffer";
import { cn } from "../lib/utils";
import { loadWallet, usableWalletCount, WALLET_EVENT } from "../lib/giftCardWallet";
import {
  CardShell,
  GoldButton,
  IconCircle,
  INK_TEXT,
  InkButton,
  LineButton,
  MUTED_TEXT,
  Pill,
  Section,
  SectionTitle,
} from "./home/ui";

export type SpecialOffersVariant = "guest" | "before" | "stay" | "after";

interface SpecialOffersProps {
  variant?: SpecialOffersVariant;
  className?: string;
}

const SpecialOffers: React.FC<SpecialOffersProps> = ({ variant = "guest", className }) => {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.language || "it");
  const rich = variant === "stay" || variant === "after";
  const titleId = `home-offers-title-${variant}`;

  const eyebrow = rich ? t("home.offers.stayEyebrow") : t("home.offers.guestEyebrow");
  const title = variant === "guest" ? t("home.offers.guestTitle") : t("home.offers.stayTitle");
  const OfferButton = rich ? InkButton : LineButton;

  // Le gift card salvate sul telefono (pages/MyGiftCards): il collegamento le conta.
  const [walletCount, setWalletCount] = useState(() => usableWalletCount(loadWallet()));
  useEffect(() => {
    const refresh = () => setWalletCount(usableWalletCount(loadWallet()));
    window.addEventListener(WALLET_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(WALLET_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const prices: { key: "weekend" | "week"; amount: number }[] = [
    { key: "weekend", amount: ROMANTIC_PRICES.weekend },
    { key: "week", amount: ROMANTIC_PRICES.week },
  ];

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle id={titleId} eyebrow={eyebrow} title={title} />

      {/* Fuga romantica */}
      <CardShell as="article" className="overflow-hidden">
        {rich ? (
          <div className="relative h-[170px] bg-nva-parchment dark:bg-stone-800">
            {/* I due calici stanno nella parte bassa della foto: la si guarda da lì. */}
            <img src={ROMANTIC_IMAGE} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[50%_70%]" />
            <Pill tone="bone" className="absolute left-3 top-3">{t("home.offers.special")}</Pill>
          </div>
        ) : null}
        <div className="flex flex-col gap-2.5 p-3.5">
          <div className="flex flex-col gap-0.5">
            <h3 className={cn("m-0 font-display font-bold leading-tight", rich ? "text-[21px]" : "text-[20px]", INK_TEXT)}>
              {t("specialOffers.romanticEscape.title")}
            </h3>
            <p className={cn("m-0 text-[13px] leading-snug", MUTED_TEXT)}>{t("specialOffers.romanticEscape.description")}</p>
          </div>
          <dl className="m-0 grid grid-cols-2 gap-2">
            {prices.map((p) => (
              <div key={p.key} className="flex flex-col rounded-xl bg-nva-cream px-3 py-2.5 dark:bg-stone-800">
                <dt className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-nva-muted dark:text-stone-400">
                  {t(`home.offers.${p.key}`)}
                </dt>
                <dd className={cn("m-0 font-display text-[22px] font-bold tabular-nums", INK_TEXT)}>
                  {formatWholeEuro(p.amount, locale)}
                </dd>
              </div>
            ))}
          </dl>
          <OfferButton full to={ROMANTIC_PATH}>
            {t("home.offers.discover")}
          </OfferButton>
        </div>
      </CardShell>

      {/* Gift card */}
      <CardShell as="article" className="flex items-center gap-3 p-3.5">
        <IconCircle tone="ink" size={46}>
          <Gift />
        </IconCircle>
        <div className="flex min-w-0 flex-grow flex-col gap-px">
          <h3 className={cn("m-0 text-[15px] font-bold leading-snug", INK_TEXT)}>{t("specialOffers.giftCard.title")}</h3>
          <p className={cn("m-0 text-[13px] leading-snug", MUTED_TEXT)}>{t("home.offers.giftDesc")}</p>
          <Link
            to="/my-gift-cards"
            className="mt-0.5 self-start rounded text-[13px] font-bold text-nva-gold-text underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-gold dark:text-nva-gold-dark"
          >
            {walletCount > 0 ? t("giftWallet.homeLinkCount", { count: walletCount }) : t("giftWallet.homeLink")}
          </Link>
        </div>
        <GoldButton to="/gift-card" className="flex-shrink-0 px-3">
          {t("home.offers.give")}
        </GoldButton>
      </CardShell>

      {variant === "stay" ? (
        <LineButton to="/book" full>
          {t("home.offers.bookNext")}
        </LineButton>
      ) : null}
    </Section>
  );
};

export default SpecialOffers;
